import React from "react";
import { useSelector } from "react-redux";
import { getFeedbackForm, getProfileForm } from "../../../../redux/reducer/incbotFeedbackReducer";
import {
  FeedbackStatusOption,
  resolveFeedbackStatusLabel,
  resolveRatingScalePalette,
} from "../../../../utils/helper";

interface CefrScale {
  title: string;
  markers: { label: string; pct: number }[];
  zones: { status: string; range: string; pct: number; color: string }[];
}

const EASY_SCALE: CefrScale = {
  title: "Easy (CEFR ceiling: B1)",
  markers: [
    { label: "A0", pct: 0 },
    { label: "A1", pct: 20 },
    { label: "A1+", pct: 40 },
    { label: "A2", pct: 60 },
    { label: "A2+", pct: 80 },
    { label: "B1", pct: 100 },
  ],
  zones: [
    { status: "Not Recommended", range: "A0-A1", pct: 14, color: "bg-red-500" },
    { status: "Cautiously Recommended", range: "A1+ - A2", pct: 50, color: "bg-yellow-400" },
    { status: "Recommended", range: "A2+ - B1", pct: 90, color: "bg-green-500" },
  ],
};

const HARD_SCALE: CefrScale = {
  title: "Hard (CEFR ceiling: C2)",
  markers: [
    { label: "A0", pct: 0 },
    { label: "A1", pct: 9.09 },
    { label: "A1+", pct: 18.18 },
    { label: "A2", pct: 27.27 },
    { label: "A2+", pct: 36.36 },
    { label: "B1", pct: 45.45 },
    { label: "B1+", pct: 54.54 },
    { label: "B2", pct: 63.63 },
    { label: "B2+", pct: 72.72 },
    { label: "C1", pct: 81.81 },
    { label: "C1+", pct: 90.9 },
    { label: "C2", pct: 100 },
  ],
  zones: [
    { status: "Not Recommended", range: "A0-B1", pct: 14, color: "bg-red-500" },
    { status: "Cautiously Recommended", range: "B1+ - B2+", pct: 50, color: "bg-yellow-400" },
    { status: "Recommended", range: "C1 - C2", pct: 90, color: "bg-green-500" },
  ],
};

const MEDIUM_SCALE: CefrScale = {
  title: "Medium (CEFR ceiling: B2)",
  markers: [
    { label: "A0", pct: 0 },
    { label: "A1", pct: 14.28 },
    { label: "A1+", pct: 28.57 },
    { label: "A2", pct: 42.85 },
    { label: "A2+", pct: 57.14 },
    { label: "B1", pct: 71.42 },
    { label: "B1+", pct: 85.71 },
    { label: "B2", pct: 100 },
  ],
  zones: [
    { status: "Not Recommended", range: "A0-A1+", pct: 14, color: "bg-red-500" },
    { status: "Cautiously Recommended", range: "A2 - B1", pct: 50, color: "bg-yellow-400" },
    { status: "Recommended", range: "B1+ - B2", pct: 90, color: "bg-green-500" },
  ],
};

const CefrScaleBar = ({
  scale,
  feedbackStatus,
}: {
  scale: CefrScale;
  feedbackStatus?: FeedbackStatusOption[];
}) => {
  // Empty unless the employer recoloured a verdict; the Tailwind gradient/dots below stay as-is
  // whenever every verdict is still on its default swatch.
  const { barStyle, dotStyles } = resolveRatingScalePalette(feedbackStatus);

  return (
    <div className="bg-white rounded-lg border border-gray-100 p-3 md:p-6">
      <div className="text-sm md:text-base font-semibold text-gray-800 mb-4">{scale.title}</div>

      {/* Zone labels + dots */}
      <div className="relative w-full h-10">
        {scale.zones.map((zone) => (
          <div
            key={zone.status}
            className="absolute text-center -translate-x-1/2"
            style={{ left: `${zone.pct}%` }}
          >
            <div className="text-xs max-sm:text-[8px] md:text-sm font-medium text-gray-700 whitespace-nowrap">
              {resolveFeedbackStatusLabel(zone.status, feedbackStatus)}: {zone.range}
            </div>
            <div
              className={`w-2 h-2 ${zone.color} rounded-full mx-auto`}
              style={dotStyles[zone.status]}
            />
          </div>
        ))}
      </div>

      {/* Gradient bar + ticks */}
      <div className="relative mb-6">
        <div
          className="w-full h-2 md:h-4 bg-gradient-to-r from-red-500 via-orange-400 via-yellow-400 via-yellow-300 via-green-300 to-green-500 rounded-full"
          style={barStyle}
        />
        {scale.markers
          .filter((m) => m.pct > 0 && m.pct < 100)
          .map((m) => (
            <div
              key={m.label}
              className="absolute top-0 h-2 md:h-4 -translate-x-1/2"
              style={{ left: `${m.pct}%` }}
            >
              <div className="w-0.5 h-[calc(100%+8px)] bg-gray-800" />
            </div>
          ))}
        {/* Level labels */}
        <div className="relative w-full mt-3 text-xs text-gray-600">
          {scale.markers.map((m) => (
            <span key={m.label} className="absolute -translate-x-1/2" style={{ left: `${m.pct}%` }}>
              {m.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const SCALE_MAP: Record<string, CefrScale> = {
  easy: EASY_SCALE,
  medium: MEDIUM_SCALE,
  hard: HARD_SCALE,
};

export const RatingSystem = () => {
  const feedbackForm = useSelector(getFeedbackForm);
  const profileForm = useSelector(getProfileForm);
  const difficulty: string | null = (feedbackForm as any)?.cefrDifficultyLevel ?? null;
  const scales =
    difficulty && SCALE_MAP[difficulty]
      ? [SCALE_MAP[difficulty]]
      : [EASY_SCALE, MEDIUM_SCALE, HARD_SCALE];

  return (
    <div className="p-3 md:p-6">
      <div className="md:hidden print:flex w-100 p-1 print:p-2 rounded-md border-b-4 border-teal-500 bg-teal-50 text-teal-500 text-md print:text-xl flex justify-center font-semibold mb-3 md:mb-6">
        Rating System
      </div>
      <div className="flex flex-col gap-4">
        {scales.map((scale) => (
          <CefrScaleBar
            key={scale.title}
            scale={scale}
            feedbackStatus={profileForm?.feedbackStatus}
          />
        ))}
      </div>
    </div>
  );
};
